import axios from '@/plugins/axios'
import { Module } from 'vuex'

export interface Post {
    username: string
    token: string
}

const postModule: Module<any, any> = {
    state: {
        posts: [],
        members: [],
        totalRows: 0,
    },

    actions: {
        async getPost(_, id) {
            const { data } = await axios.get('/api/posts/' + id)

            return data
        },

        // Retry Removal (Superadmins, the Details tab): attempts back to 0 and queued again. Returns the audit trail
        async retryPostRemoval(_, id) {
            const { data } = await axios.post(`/api/posts/${id}/retry-removal`, {}, { silent: true })

            return data
        },

        // A post's audit trail, for Superadmins (the post popup's Details tab)
        async getPostAudit(_, id) {
            const { data } = await axios.get(`/api/posts/${id}/audit`, { cache: false, silent: true })

            return data
        },

        async getPosts({ commit }, params) {

            const page = params?.postIds || params?.ids ? 1 : (params?.page ?? 1)
            const perPage = params?.postIds?.length ?? params.ids?.length ?? params?.perPage ?? 20

            let url = ''
            if (params?.export) {
                url += `/api/posts/export?itemPerPage=${[perPage]}&page=${page}`
            } else {
                url += `/api/posts?itemPerPage=${perPage}&page=${page}`
            }

            url += params?.post ? `&post=${params.post}` : ''
            url += params?.schedule ? `&schedule=${params.schedule}` : ''
            url += params?.content ? `&content=${params.content}` : ''
            url += params?.internal
                ? `&internal=${params.internal}`
                : ''
            url += params?.workspaceMember
                ? `&workspaceMember=${params.workspaceMember}`
                : ''
            url += params?.workspaceMembers
                ? `&workspaceMembers=${params.workspaceMembers}`
                : ''
            url += params?.profile
                ? `&profile=${params.profile}`
                : ''

            url += params?.reportId
                ? `&reportId=${params.reportId}`
                : ''

            url += params?.reportKeywords
                ? `&reportKeywords=${params.reportKeywords.join(',')}`
                : ''

            url += params?.criticalConversationReportId
                ? `&criticalConversationReportId=${params.criticalConversationReportId}`
                : ''

            url += params?.externalContentSearch
                ? `&externalContentSearch=${params.externalContentSearch}`
                : ''

            url += params?.profiles
                ? `&profiles=${params.profiles}`
                : ''
            url += params?.mentionedProfiles
                ? `&mentionedProfiles=${params.mentionedProfiles}`
                : ''
            url += params?.followsNetworkConnectionId
                ? `&followsNetworkConnectionId=${params.followsNetworkConnectionId}`
                : ''
            url += params?.network ? `&network=${params.network}` : ''
            url += params?.networkConnection
                ? `&networkConnection=${params.networkConnection}`
                : ''
            url += params?.success ? `&success=${params.success}` : ''
            url += params?.campaignId ? `&campaignId=${params.campaignId}` : ''
            
            params?.memberTypes?.forEach(memberType => url += `&memberType[]=${memberType}`)
            
            url += params?.deleted ? `&deleted=${params.deleted}` : ''
            url += params?.archived ? `&archived=${params.archived}` : ''
            url += params.showPinnedFirst ? `&showPinnedFirst=${params.showPinnedFirst}` : ''
            url += params?.postType ? `&postType=${params.postType}` : ''
            url += params?.saved ? `&saved=${params.saved}` : ''
            url += params?.hashtagSlug ? `&hashtagSlug=${params.hashtagSlug}` : ''
            url += params?.campaignTag ? `&campaignTag=${params.campaignTag}` : ''
                
            if (params?.ids?.length > 0) {
            	url += '&ids=' + params.ids.join(',')
            }

            if (params?.postIds?.length > 0) {
            	url += '&ids=' + params.postIds.join(',')
            }

            if (params?.dateRange) {
                url += params.dateRange?.from
                    ? `&publishDate[from]=${params.dateRange.from}`
                    : ''
                url += params.dateRange?.to
                    ? `&publishDate[to]=${params.dateRange.to}`
                    : ''
            }

            if (params?.statsFrom) {
                url += params.statsFrom
                    ? `&stats[from]=${params.statsFrom}`
                    : ''
                url += params.statsTo
                    ? `&stats[to]=${params.statsTo}`
                    : ''
            }

            url += params?.sortKey ? `&sort=${params.sortKey}` : ''
            url += params?.sortOrder ? `&order=${params.sortOrder}` : ''

            url += params?.statsFilter ? `&statsFilter=${params.statsFilter}` : ''

            url += params?.includeDeleted ? `&include-deleted=${params.includeDeleted}` : ''

            const { data } = await axios.get(url)

            if (!params?.export) {
                commit('setPosts', data ? data['hydra:member'] : null)
                commit('setTotalRows', data ? data['hydra:totalItems'] : 0)
            }

            return data
        },

        async emergencyDeletePost(_, params) {
            await axios.put(`/api/posts/${params.id}/emergency_delete`, {
                note: params?.note ?? null,
            })
        },

        async like(_, params) {
            await axios.put(`/api/posts/${params.id}/react`, {
                reactionType: params?.reactionType ?? null,
                workspaceMemberId: params?.workspaceMemberId,
                networkConnectionId: params?.networkConnectionId,
            })
        },

        async comment(_, params) {
            await axios.put(`/api/posts/${params.id}/comment`, {
                comment: params?.comment ?? null,
                workspaceMemberId: params?.workspaceMemberId,
                networkConnectionId: params?.networkConnectionId || null
            })
        },

        async repost(_, params) {
            await axios.put(`/api/posts/${params.id}/repost`, {
                message: params?.message ?? null,
                workspaceMemberId: params?.workspaceMemberId,
                networkConnectionId: params?.networkConnectionId || null
            })
        },

        async pinPost(_, params) {
            await axios.put(`/api/posts/${params.id}/pin`, {
                pin: params?.pin ?? false,
            })
        },

        async savePost(_, params) {
            await axios.put(`/api/posts/${params.id}/saved`, {
                save: params?.save ?? false,
                reportId: params?.reportId,
                reportType: params?.reportType,
            })
        },
    },

    mutations: {
        setPosts(state: any, posts) {
            state.posts = posts
        },
        setTotalRows(state: any, rows: number) {
            state.totalRows = rows
        },
    },
}

export default postModule
